import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { IconPlus, IconMinus, IconHelp, IconPlay } from "../components/icons";
import ColorSwatches from "../components/ColorSwatches";
import ProductGallery from "../components/ProductGallery";
import StarRating from "../components/StarRating";
import SpecsPanel from "../components/SpecsPanel";
import ItemAttributes from "../components/ItemAttributes";
import WarehouseStock from "../components/WarehouseStock";
import { CatalogController } from "../controllers/catalog";
import { InventoryController } from "../controllers/inventory";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { useSiteMode } from "../context/SiteModeContext";
import { effectivePrice } from "../lib/pricing";
import { variantSku } from "../lib/variant";
import { track } from "../lib/analytics";
import styles from "./ProductDetail.module.css";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const { effectiveMode, isB2B } = useSiteMode();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [available, setAvailable] = useState(null);
  const [stockInfo, setStockInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProduct(null);
    CatalogController.get(id).then((p) => {
      if (cancelled) return;
      setProduct(p || null);
      if (p?.colors?.length) setColor(p.colors[0]);
      if (p?.sizes?.length) setSize(p.sizes[0]);
    }).catch(() => { if (!cancelled) setProduct(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Refetch availability whenever the variant changes. Availability lives in
  // a custom record (B2B subset of inventory in a single location), not on
  // the item record.
  useEffect(() => {
    if (!product) return;
    const v = variantSku(product, color, size);
    if (!v) { setAvailable(null); setStockInfo(null); return; }
    let cancelled = false;
    InventoryController.get(v, effectiveMode)
      .then((r) => {
        if (cancelled) return;
        setAvailable(r?.available ?? null);
        setStockInfo(r || null);
      })
      .catch(() => { if (!cancelled) { setAvailable(null); setStockInfo(null); } });
    return () => { cancelled = true; };
  }, [product, color, size, effectiveMode]);

  if (loading) {
    return <div className={styles.page}><div className={styles.loading}>Loading product…</div></div>;
  }
  if (!product) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div style={{ fontSize: "1.4rem", marginBottom: 12 }}>Product not found</div>
          <div style={{ fontSize: "0.9rem" }}>The item “{id}” isn’t in the catalog.</div>
        </div>
      </div>
    );
  }

  // Per-color gallery wins; otherwise fall back to the product gallery with
  // the color image bumped to first.
  const fallbackGallery = (product.gallery && product.gallery.length)
    ? product.gallery
    : [product.image].filter(Boolean);
  const galleryForColor = (color?.gallery && color.gallery.length)
    ? color.gallery
    : color?.image
      ? [color.image, ...fallbackGallery.filter((g) => (typeof g === "string" ? g : g.src) !== color.image)]
      : fallbackGallery;
  const colorName = color?.name || product.colors?.[0]?.name || "";
  const priced = effectivePrice(product, user, { color, siteMode: effectiveMode });
  const sku = variantSku(product, color, size);

  return (
    <article className={styles.page}>
      <div className={styles.top}>
        <div className={styles.galleryCol}>
          <ProductGallery
            images={galleryForColor}
            caption={colorName ? `${product.name} - ${colorName}` : null}
          />
        </div>

        <div className={styles.info}>
          <h1 className={styles.title}>{product.name}</h1>

          {(product.rating != null || product.reviewCount != null) && (
            <div className={styles.ratingRow}>
              <StarRating value={product.rating || 0} count={product.reviewCount} />
              <a className={styles.reviewLink} href="#reviews">Write a review</a>
            </div>
          )}

          <div className={styles.metaRow}>
            <span className={styles.sku}>SKU: {sku}</span>
            {product.youtubeUrl && (
              <a
                href={product.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.videoChip}
                aria-label="Watch product video"
              >
                <span className={styles.videoIcon} aria-hidden><IconPlay size={10} /></span>
                Watch product video
              </a>
            )}
          </div>
          {product.shortDescription && (
            <p className={styles.shortDesc}>{product.shortDescription}</p>
          )}

          {product.colors?.length ? (
            <div className={styles.field}>
              <div className={styles.colorRow}>
                <span className={styles.label}>COLOR</span>
                {colorName && <span className={styles.colorName}>— {colorName.toUpperCase()}</span>}
              </div>
              <ColorSwatches
                colors={product.colors}
                selectedId={color?.id}
                onSelect={setColor}
                size="lg"
              />
            </div>
          ) : null}

          {product.sizes?.length ? (
            <div className={styles.field}>
              <div className={styles.label}>SIZE</div>
              <div className={styles.sizeRow}>
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={[styles.sizeChip, size === s ? styles.sizeChipActive : ""].join(" ")}
                    onClick={() => setSize(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {product.fittingArticleSlug && (
            <Link
              to={`/articles/${product.fittingArticleSlug}`}
              className={styles.fitting}
            >
              <IconHelp size={16} />
              <span><strong>Questions about fitting and sizing?</strong><br/>Read the guide</span>
            </Link>
          )}

          <div className={styles.price}>{formatPrice(priced)}</div>
          {isB2B && user?.priceLevel && user.priceLevel !== "base" && (
            <div className={styles.priceLevelTag}>
              Your {user.priceLevel} pricing applied
            </div>
          )}
          <div className={styles.field}>
            <div className={styles.label}>QTY</div>
            <div className={styles.qtyRow}>
              <div className={styles.qtyBox}>
                <IconButton size="small" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="decrease">
                  <IconMinus size={16} />
                </IconButton>
                <TextField
                  size="small"
                  variant="standard"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                  inputProps={{ inputMode: "numeric", style: { textAlign: "center", width: 40 } }}
                  InputProps={{ disableUnderline: true }}
                />
                <IconButton size="small" onClick={() => setQty((q) => q + 1)} aria-label="increase">
                  <IconPlus size={16} />
                </IconButton>
              </div>
              <Button
                variant="contained"
                size="large"
                className={styles.addBtn}
                disabled={available != null && available <= 0}
                onClick={() => {
                  add(sku, qty, {
                    name: product.name,
                    image: color?.image || product.image,
                    price: priced?.price?.amount,
                    color: color ? { name: color.name, hex: color.hex, image: color.image } : null,
                    size,
                  });
                  track("add_to_cart", { sku, qty, color: color?.id, size });
                }}
              >
                {available != null && available <= 0 ? "Out of Stock" : "Add to Cart"}
              </Button>
            </div>
            {stockInfo?.warehouses ? (
              <div className={styles.availability}>
                <WarehouseStock availability={stockInfo} layout="row" size="md" />
              </div>
            ) : available != null ? (
              <div className={styles.availability}>
                {available > 0
                  ? `${available} available${available <= 5 ? " — limited stock" : ""}`
                  : "Currently out of stock"}
              </div>
            ) : null}
            <a className={styles.wishlist} href="#wishlist">Add to Wishlist</a>
          </div>
        </div>
      </div>

      {product.tagline && (
        <div className={styles.tagline}>{product.tagline}</div>
      )}
      {product.description && (
        <Typography component="p" className={styles.body}>{product.description}</Typography>
      )}
      {(product.attributes?.sections?.length || product.specs?.length) ? (
        <section className={styles.detailSplit}>
          <div className={styles.detailLeft}>
            <ItemAttributes
              sections={product.attributes?.sections || []}
              defaultOpen={product.attributes?.defaultOpen ?? null}
            />
          </div>
          <div className={styles.detailRight}>
            <SpecsPanel
              specs={product.specs || []}
              origin={product.origin}
            />
          </div>
        </section>
      ) : null}
    </article>
  );
}
